import {
  LinkAccount,
  getBankCodes,
  saveAccount,
  updateProperty,
  changePaymentMethodRequest,
} from "@services/krent.service";
import { Modal } from "@shared/controls/Modal";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";

export type Bank = {
  value: string;
  label: string;
};

type Prop = {
  id?: string;
  type: "personal" | "landlord";
  propertyId?: string;
  onClose?: any;
  previousLocation?: string;
  show?: boolean;
  onAccountSaved?: () => void;
  isChangeRequest?: boolean;
};

const AddBankAccount = ({
  id,
  type,
  propertyId,
  onClose,
  previousLocation,
  show = false,
  onAccountSaved,
  isChangeRequest = false,
}: Prop) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [accountNumber, setAccountNumber] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [pin, setPin] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [banks, setBanks] = useState<Bank[]>([]);
  const [filteredBanks, setFilteredBanks] = useState<Bank[]>([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [accountSuccess, setAccountSuccess] = useState("");
  const [accountError, setAccountError] = useState("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isVerifying, setIsVerifiying] = useState<boolean>(false);
  const [changeRequestResponse, setChangeRequestResponse] = useState<any>(null);
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);
  const handleGetBanks = () => {
    setBankLoading(true);
    getBankCodes()
      .then((resp: any) => {
        const formattedBanks = resp.data.map((bank: any) => ({
          value: bank.code,
          label: bank.name,
        }));
        setBanks(formattedBanks);
        setFilteredBanks(formattedBanks);
        setBankLoading(false);
      })
      .catch((err) => console.log(err));
  };

  const handleVerifyAccount = () => {
    let abortController = new AbortController();
    setIsVerifiying(true);
    LinkAccount({ accountNumber, bankCode }, abortController.signal)
      .then((resp: any) => {
        setAccountName(resp.data.account_name);
      })
      .catch((err) => {
        setAccountName("Not Found");
        toast.error(err.errors[0].message || "Failed to Verify Account");
      })
      .finally(() => {
        setIsVerifiying(false);
      });
  };

  const handleSaveAccount = (e: any) => {
    e.preventDefault();
    let abortController = new AbortController();
    const clearForm = () => {
      setIsSaving(false);
      setAccountName("");
      setAccountNumber("");
      setAccountType("");
      setPin("");
      setSortCode("");
      setBankCode("");
    };
    setIsSaving(true);

    const data = {
      accountName,
      accountNumber,
      bankName,
      accountType,
      ...(type === "personal" && { pin }),
      ...(type === "personal" && sortCode && { sortCode }),
      ...(type === "landlord" && sortCode && { sortCode }),
    };
    if (type === "personal") {
      if (isChangeRequest) {
        changePaymentMethodRequest(id!, data, abortController.signal)
          .then((resp: any) => {
            if (resp.success) {
              setChangeRequestResponse(resp.data);
              setShowChangeRequestModal(true);
              if (onAccountSaved) {
                onAccountSaved();
              }
            }
          })
          .catch((e) => {
            setAccountError(
              e.errors?.[0]?.message || "Failed to submit change request",
            );
          })
          .finally(() => {
            clearForm();
          });
      } else {
        saveAccount(
          {
            id,
            data,
          },
          abortController.signal,
        )
          .then((resp: any) => {
            if (resp.success) {
              setAccountSuccess("Bank Account updated sucessfully");
              if (onAccountSaved) {
                onAccountSaved();
              }

              if (
                location.state &&
                (location.state as any).flow === "messaging-payment"
              ) {
                navigate(`/messaging/${(location.state as any).messagingId}`, {
                  replace: true,
                });
              } else if (location.state && (location.state as any).from) {
                navigate((location.state as any).from, { replace: true });
              } else if (previousLocation) {
                navigate(previousLocation);
              } else {
                onClose();
              }
            }
          })
          .catch((e) => {
            setAccountError(e.errors[0].message);
          })
          .finally(() => {
            clearForm();
          });
      }
    } else if (type === "landlord") {
      updateProperty(propertyId, { landlordAccountDetails: data })
        .then((resp) => {
          if (resp.success) {
            setAccountSuccess("Bank Account updated sucessfully");
            if (previousLocation) {
              navigate(previousLocation);
            } else {
              onClose();
            }
          }
        })
        .catch((error: any) => {
          setAccountError(error.errors[0].message);
        })
        .finally(() => {
          clearForm();
        });
    }
  };

  const handleAccountNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setAccountNumber(e.target.value);
  };

  const handleBankChange = (selectedBank: Bank | null) => {
    if (selectedBank) {
      setBankName(selectedBank.label);
      setBankCode(selectedBank.value);
    } else {
      setBankCode("");
    }
  };

  const handleBankSearch = (searchTerm: string) => {
    const filtered = banks.filter((bank) =>
      bank.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredBanks(filtered);
  };

  const handleAccountType = (e: any) => {
    setAccountType(e.target.value);
  };

  useEffect(() => {
    handleGetBanks();
  }, []);
  return (
    <>
      <Modal
        id="addBank"
        show={show}
        onClose={onClose}
        className="modal-rounded"
        size={570}
      >
        <style>{`
        .modal-rounded .modal-content {
          border-radius: 24px !important;
        }
      `}</style>
        {bankLoading ? (
          <h4 className=" pb-5">Loading...</h4>
        ) : (
          <div
            className="pb-5"
            style={{
              maxHeight: "70vh",
              overflowY: "auto",
              padding: "20px",
            }}
          >
            <div className="items-center flex justify-content-center">
              <svg
                width="80"
                height="80"
                viewBox="0 0 80 80"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="80"
                  height="80"
                  rx="40"
                  fill="#47BBA9"
                  fillOpacity="0.15"
                />
                <path
                  d="M40.209 29.5885H40.0007M28.334 35.0052V51.6719M35.0007 35.0052V51.6719M45.0007 35.0052V51.6719M51.6673 35.0052V51.6719M40.4173 29.5885C40.4173 29.699 40.3734 29.805 40.2953 29.8832C40.2171 29.9613 40.1112 30.0052 40.0007 30.0052C39.8901 30.0052 39.7842 29.9613 39.706 29.8832C39.6279 29.805 39.584 29.699 39.584 29.5885C39.584 29.478 39.6279 29.3721 39.706 29.2939C39.7842 29.2158 39.8901 29.1719 40.0007 29.1719C40.1112 29.1719 40.2171 29.2158 40.2953 29.2939C40.3734 29.3721 40.4173 29.478 40.4173 29.5885Z"
                  stroke="#0F5132"
                  strokeWidth="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M55.5869 35.0026H24.4136C24.1795 34.9993 23.9527 34.9211 23.7664 34.7794C23.5801 34.6378 23.4441 34.4402 23.3783 34.2156C23.3125 33.9909 23.3204 33.7512 23.4008 33.5314C23.4812 33.3116 23.63 33.1233 23.8252 32.9943L34.5502 25.9443C37.1952 24.2059 38.5169 23.3359 40.0002 23.3359C41.4836 23.3359 42.8052 24.2059 45.4502 25.9443L56.1752 32.9943C56.3694 33.124 56.5171 33.3123 56.5968 33.5317C56.6765 33.7512 56.6841 33.9904 56.6184 34.2144C56.5528 34.4385 56.4173 34.6358 56.2318 34.7776C56.0463 34.9193 55.8204 34.9982 55.5869 35.0026ZM55.0669 53.8243L53.9202 52.6459C53.4519 52.1626 53.2169 51.9226 52.9202 51.7959C52.6202 51.6693 52.2902 51.6693 51.6269 51.6693H28.3736C27.7102 51.6693 27.3786 51.6693 27.0819 51.7959C26.7819 51.9226 26.5486 52.1626 26.0819 52.6459L24.9336 53.8243C23.7569 55.0343 23.1669 55.6409 23.3752 56.1543C23.5819 56.6693 24.4152 56.6693 26.0802 56.6693H53.9202C55.5869 56.6693 56.4202 56.6693 56.6252 56.1543C56.8336 55.6409 56.2436 55.0343 55.0669 53.8243Z"
                  stroke="#0F5132"
                  strokeWidth="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div
              className="flex justify-between items-center mb-4 "
              style={{ justifySelf: "center" }}
            >
              <h4
                style={{
                  color: "#727272",
                }}
              >
                {type === "personal" ? "Bank Account" : "Add Landlord Account"}
              </h4>
            </div>
            <form onSubmit={handleSaveAccount}>
              <div className="mb-3">
                <label htmlFor="accountNumber" className="form-label fw-500">
                  Account Number
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="accountNumber"
                  value={accountNumber}
                  onChange={handleAccountNumberChange}
                  required
                  style={{ borderRadius: "8px" }}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="bankSelect" className="form-label fw-500">
                  Bank
                </label>
                <Select
                  id="bankSelect"
                  options={filteredBanks}
                  value={filteredBanks.find((bank) => bank.value === bankCode)}
                  onChange={handleBankChange}
                  onInputChange={(inputValue: any) =>
                    handleBankSearch(inputValue)
                  }
                  isSearchable
                  styles={{
                    control: (provided: any) => ({
                      ...provided,
                      borderRadius: "8px",
                      minHeight: "38px",
                    }),
                  }}
                />
              </div>

              <button
                type="button"
                className="btn btn-primary mb-3"
                onClick={handleVerifyAccount}
                disabled={!accountNumber || !bankCode || isVerifying}
                style={{ borderRadius: "8px" }}
              >
                {isVerifying ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  "Verify Account"
                )}
              </button>

              <div className="mb-3">
                <label htmlFor="accountName" className="form-label fw-500">
                  Account Name
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="accountName"
                  value={accountName}
                  readOnly
                  style={{ borderRadius: "8px" }}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="accountType" className="form-label fw-500">
                  Account Type
                </label>

                <select
                  className="form-control form-select"
                  name="accountType"
                  id="accountType"
                  value={accountType}
                  onChange={handleAccountType}
                  required
                  style={{ borderRadius: "8px" }}
                >
                  <option value="">Select</option>
                  <option value="Savings">Savings</option>
                  <option value="Current">Current</option>
                </select>
              </div>
              {/* <div className="mb-3">
              <label htmlFor="sortCode" className="form-label fw-500">
                Sort Code
              </label>
              <input
                type="text"
                className="form-control"
                id="sortCode"
                value={sortCode}
                onChange={(e: any) => setSortCode(e.target.value)}
                style={{ borderRadius: "8px" }}
              />
              <span className="text-danger fs-10">Optional</span>
            </div> */}
              {type === "personal" && (
                <div className="mb-3">
                  <label htmlFor="pin" className="form-label fw-500">
                    Transaction Pin
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="pin"
                    value={pin}
                    onChange={(e: any) => setPin(e.target.value)}
                    required
                    style={{ borderRadius: "8px" }}
                  />
                </div>
              )}
              {accountSuccess ? (
                <p className="text-success mb-3">{accountSuccess}</p>
              ) : accountError ? (
                <p className="text-danger mb-3">{accountError}</p>
              ) : (
                ""
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  !accountName || (type === "personal" && !pin) || !accountType
                }
                style={{ borderRadius: "8px" }}
              >
                {isSaving ? (
                  <span className="spinner-border spinner-border-sm"></span>
                ) : (
                  "Save Account"
                )}
              </button>
            </form>
          </div>
        )}
      </Modal>

      {/* Change Request Confirmation Modal */}
      <Modal
        show={showChangeRequestModal}
        onClose={() => {
          setShowChangeRequestModal(false);
          onClose();
        }}
        size={500}
      >
        <div className="p-4">
          <div className="text-center mb-4">
            <div
              className="mb-3"
              style={{
                width: "60px",
                height: "60px",
                background: "#d1fae5",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
              }}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#059669"
                strokeWidth="2"
              >
                <path
                  d="M22 11.08V12a10 10 0 1 1-5.93-9.14"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <polyline
                  points="22 4 12 14.01 9 11.01"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h5 className="fw-bold mb-2">Change Request Submitted</h5>
            <p className="text-muted mb-0">
              Your payment method change request is pending approval
            </p>
          </div>

          {changeRequestResponse && (
            <div className="mb-4">
              <div className="mb-3">
                <small className="text-muted fw-500">
                  CURRENT PAYMENT METHOD
                </small>
                <div className="mt-2 p-3 bg-light rounded">
                  <p className="mb-1 fw-bold">
                    {changeRequestResponse.currentPaymentMethod.accountName}
                  </p>
                  <p className="mb-1">
                    {changeRequestResponse.currentPaymentMethod.bankName}
                  </p>
                  <p className="mb-0">
                    {changeRequestResponse.currentPaymentMethod.accountNumber}
                  </p>
                </div>
              </div>

              <div className="mb-3">
                <small className="text-muted fw-500">
                  REQUESTED PAYMENT METHOD
                </small>
                <div className="mt-2 p-3 bg-light rounded border border-success">
                  <p className="mb-1 fw-bold">
                    {changeRequestResponse.requestedPaymentMethod.accountName}
                  </p>
                  <p className="mb-1">
                    {changeRequestResponse.requestedPaymentMethod.bankName}
                  </p>
                  <p className="mb-0">
                    {changeRequestResponse.requestedPaymentMethod.accountNumber}
                  </p>
                </div>
              </div>
            </div>
          )}

          <button
            className="btn btn-primary w-100"
            onClick={() => {
              setShowChangeRequestModal(false);
              onClose();
            }}
            style={{ borderRadius: "8px" }}
          >
            Close
          </button>
        </div>
      </Modal>
    </>
  );
};
export default AddBankAccount;
